// Script import from the app (F4, BACKEND-GAPS.md gap 6): paste or upload a script into an episode; the server
// stores it as a script revision, breaks it down into scenes, shots, cast, sets and props, and asks for the
// writing sign-off (core/api/src/routes/script.ts). The Fountain parser answers at once (201, the result); Claude
// (when the server has a key) runs as a job (202, a job id), followed here on GET /episodes/{id}/script.
import type { Api } from "../api.ts";

export type ScriptFormat = "fountain" | "text" | "pipeline";

/** What the server can import (GET /script-import). */
export interface ScriptImportInfo {
  formats: ScriptFormat[];
  /** Claude can prepare the breakdown (an Anthropic key is set) */
  ai: boolean;
}

export interface ScriptCounts {
  scenes: number;
  shots: number;
  characters: number;
  locations: number;
  props: number;
  dialogue_lines?: number;
}

/** What an import says when it is done. */
export interface ScriptImported {
  revNo: number | null;
  /** false: the bytes equal the latest revision; nothing new */
  created: boolean;
  by: "ai" | "parser" | null;
  counts: ScriptCounts | null;
  warnings: string[];
  /** names asked for the writing sign-off are not known here; the board reloads */
  askedSignoff: boolean;
}

interface RawImport {
  job_id?: string | null;
  revision?: { rev_no?: number; created?: boolean } | null;
  breakdown?: "ai" | "parser";
  counts?: ScriptCounts;
  warnings?: string[];
  signoff_request?: { id: string } | null;
}

export async function scriptImportInfo(api: Api, ws: string): Promise<ScriptImportInfo | null> {
  try {
    const r = await api.request<{ formats?: string[]; ai?: boolean }>(
      "GET",
      `/workspaces/${ws}/script-import`,
    );
    const formats = (r.formats ?? []).filter(
      (f): f is ScriptFormat => f === "fountain" || f === "text" || f === "pipeline",
    );
    return formats.length ? { formats, ai: !!r.ai } : null;
  } catch {
    // an older server without the route: the app cannot import scripts
    return null;
  }
}

/** The format a file name says: .fountain and .md read as Fountain, .txt as plain text. */
export function formatOfFile(name: string): ScriptFormat {
  return /\.txt$/i.test(name) ? "text" : "fountain";
}

const SCRIPT_FILE = /\.(fountain|spmd|txt|md|markdown)$/i;
export const isScriptFile = (name: string) => SCRIPT_FILE.test(name);
export const SCRIPT_ACCEPT = ".fountain,.spmd,.txt,.md,text/plain,text/markdown";

/** GET /episodes/{id}/script: the latest revision and the latest AI breakdown job. */
interface EpisodeScript {
  revision: { rev_no: number } | null;
  job: {
    id: string;
    state: "queued" | "running" | "succeeded" | "failed" | "cancelled";
    error: { code: string; message?: string } | null;
    result: RawImport | null;
  } | null;
}

export function fromRaw(r: RawImport): ScriptImported {
  return {
    revNo: r.revision?.rev_no ?? null,
    created: r.revision?.created ?? true,
    by: r.breakdown ?? null,
    counts: r.counts ?? null,
    warnings: r.warnings ?? [],
    askedSignoff: !!r.signoff_request,
  };
}

/**
 * A script import refused by the server (modules/production, breakdown/import.ts and pipeline.ts), in plain words;
 * null for a code it does not know, which is then said as the server put it.
 */
export function scriptImportErrorText(code: string | null | undefined): string | null {
  switch (code) {
    case "no_scene_headings":
      return "This text has no scene headings (INT. or EXT.), so it can’t be split into scenes, and bringing it in would replace the episode’s whole breakdown. Add scene headings first.";
    case "script_too_large":
      return "This script is over 1 MB, more than a script import takes.";
    case "bad_script":
      return "This text could not be read as a script. Check that it is Fountain or plain screenplay text.";
    case "bad_breakdown":
      return "The breakdown of this script does not fit the production: a name in it is already used for something else (a character named like a set, for example). Rename it in the text and try again.";
    case "shot_code_collision":
      return "A shot this script would make already belongs to another episode. Give this episode its own code first.";
    case "not_configured":
      return "This server has no file storage set up, so it can’t keep a script yet.";
    case "script_import_superseded":
      return "A newer script came in for this episode while this one was being read; the newer one counts.";
    default:
      return null;
  }
}

/** A script import error in plain words: the known codes as sentences, anything else as the server said it. */
export function scriptErrorText(e: unknown): string | null {
  const code = e && typeof e === "object" && "code" in e ? (e as { code: unknown }).code : null;
  return typeof code === "string" ? scriptImportErrorText(code) : null;
}

/** Why a breakdown job did not finish, in plain words. */
function jobError(e: { code: string; message?: string } | null): string {
  if (e?.code === "model_rate_limited" || e?.code === "model_unavailable")
    return "Claude could not be reached. Try again in a moment, or read the Fountain instead.";
  if (e?.code === "model_refused")
    return "Claude would not break this script down. Read the Fountain instead.";
  const known = scriptImportErrorText(e?.code);
  if (known) return known;
  return `The breakdown did not finish${e?.message ? `: ${e.message}` : "."}`;
}

/** The newest script version of an episode (GET /episodes/{id}/script): its number, or null when it has none. */
export async function latestScriptRev(api: Api, ws: string, episodeId: string): Promise<number | null> {
  const s = await api.request<EpisodeScript>("GET", `/workspaces/${ws}/episodes/${episodeId}/script`);
  return s.revision?.rev_no ?? null;
}

/**
 * Import a script into an episode. A job answer (Claude) is followed on GET /episodes/{id}/script every 3 s until
 * the job succeeds or fails (up to `timeoutMs`); `onPhase` says what is going on for the progress line.
 */
export async function importScript(
  api: Api,
  ws: string,
  episodeId: string,
  input: { format: ScriptFormat; text: string; note?: string; ai?: boolean },
  o: {
    onPhase?: (p: "sending" | "reading") => void;
    timeoutMs?: number;
    pollMs?: number;
    signal?: AbortSignal;
  } = {},
): Promise<ScriptImported> {
  o.onPhase?.("sending");
  const r = await api.request<RawImport>("POST", `/workspaces/${ws}/episodes/${episodeId}/script`, {
    body: {
      format: input.format,
      text: input.text,
      ...(input.note?.trim() ? { note: input.note.trim() } : {}),
      ...(input.ai !== undefined ? { ai: input.ai } : {}),
    },
  });
  if (!r.job_id) return fromRaw(r);
  o.onPhase?.("reading");
  const until = Date.now() + (o.timeoutMs ?? 15 * 60_000);
  while (Date.now() < until && !o.signal?.aborted) {
    await new Promise((ok) => setTimeout(ok, o.pollMs ?? 3000));
    const s = await api
      .request<EpisodeScript>("GET", `/workspaces/${ws}/episodes/${episodeId}/script`)
      .catch(() => null);
    const job = s?.job;
    if (!job || job.id !== r.job_id) continue;
    if (job.state === "succeeded")
      return job.result
        ? fromRaw(job.result)
        : {
            revNo: s.revision?.rev_no ?? null,
            created: true,
            by: "ai",
            counts: null,
            warnings: [],
            askedSignoff: true,
          };
    if (job.state === "failed" || job.state === "cancelled") throw new Error(jobError(job.error));
  }
  throw new Error("Claude is still reading the script. It shows up here as soon as it lands.");
}

/** One script version's text (GET /episodes/{id}/script/versions/{rev}); text is null when none was stored. */
export interface ScriptText {
  rev_no: number;
  note: string | null;
  created_at: string;
  format: ScriptFormat | null;
  text: string | null;
  /** the script is longer than the server reads: text holds its first part */
  truncated?: boolean;
}

export function readScriptVersion(api: Api, ws: string, episodeId: string, rev: number): Promise<ScriptText> {
  return api.request<ScriptText>("GET", `/workspaces/${ws}/episodes/${episodeId}/script/versions/${rev}`);
}

/**
 * A Fountain title page (Title: ..., Author: ... up to the first blank line) apart from the script under it, so
 * the reader can show it as one quiet line instead of as action.
 */
export function splitTitlePage(text: string): { title: Record<string, string>; body: string } {
  const src = text.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n");
  const first = src.split("\n", 1)[0] ?? "";
  if (!/^[A-Za-z][A-Za-z ]{0,30}:/.test(first) || /^(INT|EXT|EST|I\/E)[.\s]/i.test(first))
    return { title: {}, body: src };
  const end = src.search(/\n[ \t]*\n/);
  const head = end < 0 ? src : src.slice(0, end);
  const title: Record<string, string> = {};
  let key: string | null = null;
  for (const line of head.split("\n")) {
    const m = /^([A-Za-z][A-Za-z ]{0,30}):\s*(.*)$/.exec(line);
    if (m) {
      key = m[1]!.trim().toLowerCase();
      title[key] = m[2]!.trim();
    } else if (key && line.trim()) title[key] = `${title[key] ? `${title[key]} ` : ""}${line.trim()}`;
  }
  return { title, body: end < 0 ? "" : src.slice(end).replace(/^\n+/, "") };
}
